/**
 * Compliance — obligations materialised every period regardless of evidence,
 * then settled by a rule; readiness by framework; and which rules are live.
 *
 * Shapes follow `insight.py` (`compliance`, `rule_status`) and
 * `insight/obligations.py`. An obligation that only appears once it is met
 * is a register that can never show what is missing, so pending and overdue
 * rows are the point of this screen.
 */

export const OBLIGATION_STATUSES = ["overdue", "pending", "met", "waived"] as const;
export type ObligationStatus = (typeof OBLIGATION_STATUSES)[number];

export const OBLIGATION_STATUS_LABEL: Record<ObligationStatus, string> = {
  overdue: "Overdue",
  pending: "Pending",
  met: "Met",
  waived: "Waived",
};

export interface Readiness {
  framework: string;
  total: number;
  met: number;
  overdue: number;
  /** 0–100, as the platform rounds it. */
  pct: number;
}

export interface Obligation {
  framework: string;
  title: string;
  /** daily | weekly | monthly | quarterly | annual | event */
  period?: string;
  /** '2026-08-30', '2026-W35', '2026-Q3' — one row per period, not per run. */
  periodKey?: string;
  status: ObligationStatus;
  dueAt?: string;
  metAt?: string;
  evidenceCount: number;
}

export interface ComplianceReport {
  readiness: Readiness[];
  obligations: Obligation[];
}

export interface Rule {
  key: string;
  name: string;
  /** regulatory | safety | operational | predictive … as the pack classes it. */
  ruleClass?: string;
  kind?: string;
  severity: number;
  enabled: boolean;
  verifyRequired: boolean;
  parameters: Record<string, unknown>;
  packDefaults: Record<string, unknown>;
  confirmedAgainst?: string;
  verifiedAt?: string;
  reference?: string;
  trigger?: string;
  /** Why a rule is not live; null when it is. */
  gatedReason?: string;
}

export function overallReadiness(rows: readonly Readiness[]): Readiness | null {
  if (!rows.length) return null;
  const total = rows.reduce((t, r) => t + r.total, 0);
  const met = rows.reduce((t, r) => t + r.met, 0);
  const overdue = rows.reduce((t, r) => t + r.overdue, 0);
  return { framework: "All frameworks", total, met, overdue, pct: total ? Math.round((1000 * met) / total) / 10 : 0 };
}

/** Overdue first, then pending by due date, then met, then waived. */
export function sortObligations(rows: readonly Obligation[]): Obligation[] {
  const rank: Record<ObligationStatus, number> = { overdue: 0, pending: 1, met: 2, waived: 3 };
  return [...rows].sort(
    (a, b) =>
      rank[a.status] - rank[b.status] ||
      (a.dueAt ?? "9").localeCompare(b.dueAt ?? "9") ||
      a.title.localeCompare(b.title),
  );
}

/** Parameters this tenant changed from the pack's defaults. */
export function changedParameters(r: Rule): string[] {
  return Object.keys(r.parameters).filter((k) => JSON.stringify(r.parameters[k]) !== JSON.stringify(r.packDefaults[k]));
}

/** Mirrors the platform's `VerifyIn`. */
export interface VerifyRuleInput {
  ruleKey: string;
  /** Overrides on the pack's parameters; unchanged ones may be omitted. */
  parameters: Record<string, unknown>;
  /** What the values were confirmed against — the certification basis, an AIP entry, a calibration. Required. */
  confirmedAgainst: string;
}

export interface VerifyRuleResult {
  key: string;
  enabled: boolean;
  parameters: Record<string, unknown>;
  packDefaults: Record<string, unknown>;
  confirmedAgainst: string;
  verifiedAt?: string;
  note?: string;
}

/** Parse a parameter typed into a field back to the type the pack declared for it. */
export function coerceParameter(text: string, like: unknown): unknown {
  const s = text.trim();
  if (typeof like === "number") {
    const n = Number(s);
    return Number.isFinite(n) ? n : text;
  }
  if (typeof like === "boolean") return s === "true";
  if (like !== null && typeof like === "object") {
    try {
      return JSON.parse(s);
    } catch {
      return text;
    }
  }
  return text;
}
